import { type ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { type AnalyticsBiQueryView } from '@vello/api-client';
import { AUTO_REFRESH_MS, BarList, PageHeader, Panel, SectionTitle, TabPanel, Tabs, Notice, Text, Row, Stack, Grid, useAutoRefresh } from '@vello/web-theme';
import {
  BI_PANELS,
  BI_PANEL_IDS,
  DEFAULT_PERIOD,
  EMPTY_FILTER_STATE,
  type BiCompareChoice,
  type BiFilterState,
  type BiPanelId,
  type BiPeriod,
  type CrossFilterSelect,
  type PanelsUrlState,
  type BiUnit,
  type InsightEnv,
  activeFilterState,
  applyCrossFilter,
  expectedMarkets,
  filterRequestParts,
  insightDrillState,
  panelDimensionCompat,
  panelFromSearch,
  panelGrain,
  periodValid,
  hasRegionDimension,
  scopeFilters,
  trendGrain,
} from '../../src/index';
import { type CompareAgainst } from '@vello/analytics-semantic';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { AnswerNotes, BiProblem, BreakdownChart, CompareControls, FreshnessBadge, KpiCards, KpiSkeletons, PeriodControls, TrendCharts, useBiText } from '../components/bi/bi-ui';
import { type Loadable, useBiCatalog, usePanelData, useTargets } from '../components/bi/bi-hooks';
import { InsightCard, type InsightContext } from '../components/bi/InsightCharts';
import { openInExplorer } from '../components/bi/bi-reports-store';
import { useRouter } from '../router';
import { FilterBuilder, refusalWords, type FilterRefusal } from '../components/bi/FilterBuilder';
import { FilterSetsMenu } from '../components/bi/FilterSetsMenu';
import { ShareLinkButton, UrlNotices, useUrlSync } from '../components/bi/ShareLink';

/**
 * Análises › Painéis (D522; doc 20 §4.3): the eight standard dashboards of Vello's own BI — visão geral, food, corridas, entregas,
 * Balcão, financeiro, motoristas e entregadores, clientes. Each shows its headline figures with the comparison (default: the same
 * weekday a year earlier, else the previous period), a trend per day or hour, and one or two breakdowns, for the period and the
 * scope the operator picked. Money is always per currency; the data's age is on the page; a holiday on one side of the comparison
 * is said. The reads are queued two at a time (the API's per-user limit), and nothing is cached.
 */
export function AnalyticsPanelsScreen(): ReactElement {
  const { t } = useT();
  const { scope, markets } = useAdmin();
  const { state: catalogState, reload: reloadCatalog } = useBiCatalog();
  const catalog = catalogState.status === 'ready' ? catalogState.value : undefined;
  const text = useBiText(catalog);
  const { navigate } = useRouter();
  const targets = useTargets();
  // D531: a link may name the panel (`?panel=operation`); a shared state (`?s=`) restores its own after the catalog is read.
  const [panelId, setPanelId] = useState<BiPanelId>(() => (typeof window !== 'undefined' ? panelFromSearch(window.location.search) : undefined) ?? 'overview');
  const [period, setPeriod] = useState<BiPeriod>(DEFAULT_PERIOD);
  const [{ compare, upToNowAgainst }, setCompare] = useState<{ compare: BiCompareChoice; upToNowAgainst: CompareAgainst }>({ compare: 'auto', upToNowAgainst: 'last_year_by_date' });
  // D528: the global filter bar - conditions that apply to every figure and chart of the panel.
  const [bar, setBar] = useState<BiFilterState>(EMPTY_FILTER_STATE);
  const [crossNote, setCrossNote] = useState<string | undefined>(undefined);
  // The latest bar, written synchronously: a heat cell reports its column and then its row in ONE event, and the second must build on the first.
  const barRef = useRef(bar);
  barRef.current = bar;

  const scopeKey = JSON.stringify(scope.current);
  const regionDimension = hasRegionDimension(catalog);
  const filters = useMemo(() => scopeFilters(scope.current, markets, regionDimension), [scopeKey, markets, regionDimension]); // eslint-disable-line react-hooks/exhaustive-deps -- `scopeKey` is the scope
  const panel = BI_PANELS[panelId];
  const grain = panelGrain(panel, trendGrain(period, new Date()));
  const valid = periodValid(period);
  const active = useMemo(() => {
    const a = activeFilterState({ ...bar, having: [] }, catalog);
    const parts = filterRequestParts(a);
    return { state: a, bar: { filters: parts.filters ?? [], anyOf: parts.anyOf ?? [] } };
  }, [bar, catalog]);
  const data = usePanelData(panel, { period, compare, upToNowAgainst, scope: filters, bar: active.bar }, grain, catalog !== undefined && valid);

  // Which dimensions every block of THIS panel can carry, asked of the planner itself (the server's reason code comes back with a refusal).
  const clock = useMemo(() => {
    const asOf = new Date().toISOString();
    return { asOf, today: asOf.slice(0, 10) };
  }, []);
  const compat = useCallback(
    (dimensionId: string): FilterRefusal | undefined => {
      if (catalog?.dimensions.find((d) => d.id === dimensionId)?.personal === true) return { code: 'PANEL_NO_PEOPLE' };
      const r = panelDimensionCompat(panel, dimensionId, clock);
      return r.ok ? undefined : { code: r.code };
    },
    [panel, catalog, clock],
  );
  const onCrossFilter: CrossFilterSelect = useCallback(
    (dimension, value, options) => {
      const why = compat(dimension);
      if (why !== undefined) {
        setCrossNote(t('bi.flt.cross.refused', { dim: text.dimName(dimension), code: refusalWords(text, why) }));
        return;
      }
      const r = applyCrossFilter(barRef.current, dimension, value, options);
      setCrossNote(r.changed ? undefined : t('bi.flt.cross.notApplied'));
      if (r.changed) {
        barRef.current = r.state;
        setBar(r.state);
      }
    },
    [compat, t, text],
  );
  const selectedOf = (dimension: string): readonly string[] => bar.filters.filter((f) => f.dimension === dimension && (f.op === 'in' || f.op === 'eq')).flatMap((f) => f.values);

  // D528: the link. The panel, the period, the comparison and the bar; never a person.
  const urlState = useMemo<PanelsUrlState>(() => ({ kind: 'panels', panel: panelId, period, compare, upToNowAgainst, bar: { ...active.state, having: [] } }), [panelId, period, compare, upToNowAgainst, active.state]);
  const sync = useUrlSync<PanelsUrlState>({
    ready: catalog !== undefined,
    current: urlState,
    kind: 'panels',
    onRestore: (restored) => {
      setPanelId(restored.panel);
      setPeriod(restored.period);
      setCompare({ compare: restored.compare, upToNowAgainst: restored.upToNowAgainst });
      setBar(restored.bar);
    },
  });

  const insightEnv = useMemo<InsightEnv>(
    () => ({
      grain,
      num: (unit: string, cell, currency) => text.fmt.chartValue(unit as BiUnit, cell, currency),
      targets: targets.state.status === 'ready' ? targets.state.value : [],
    }),
    [grain, text, targets.state],
  );
  const insightCtx: InsightContext = { text, env: insightEnv, selectedOf, onSelect: onCrossFilter, targets, onRetry: data.reload };

  const ready = Object.values(data.tiles).find((x): x is Extract<Loadable<AnalyticsBiQueryView>, { status: 'ready' }> => x.status === 'ready');
  const noteSource = (() => {
    const first = data.tiles['kpi:0'];
    return first?.status === 'ready' ? first.value : ready?.value;
  })();
  // The panel re-reads on its own (no refresh button); the freshness badge says how old the numbers are.
  useAutoRefresh(data.refresh, { intervalMs: AUTO_REFRESH_MS.report, enabled: catalog !== undefined && valid });
  const expected = catalog !== undefined ? expectedMarkets(scope.current, catalog.markets, markets) : [];

  return (
    <>
      <PageHeader
        title={t('bi.title')}
        subtitle={t('bi.subtitle')}
        actions={<FreshnessBadge freshness={noteSource?.freshness} />}
      />

      {catalogState.status === 'error' && (
        <Panel>
          <BiProblem error={catalogState.error} onRetry={reloadCatalog} text={text} />
        </Panel>
      )}

      {catalogState.status !== 'error' && (
        <>
          <Tabs<BiPanelId>
            id="bi-panels"
            ariaLabel={t('bi.panels')}
            value={panelId}
            onChange={setPanelId}
            tabs={BI_PANEL_IDS.map((id) => ({ id, label: t(BI_PANELS[id].labelId) }))}
          />
          <TabPanel id="bi-panels" tab={panelId} value={panelId}><Stack gap={5}>
            <Text as="p" role="caption" tone="secondary">{t(panel.subtitleId)}</Text>
            <Row gap={5} align="end" wrap>
              <PeriodControls period={period} onChange={setPeriod} idPrefix="bi-panel" />
              <CompareControls compare={compare} upToNowAgainst={upToNowAgainst} onChange={setCompare} idPrefix="bi-panel" />
            </Row>
            {!valid && <Notice tone="warning">{t('bi.period.invalid')}</Notice>}

            {catalog !== undefined && (
              <Panel>
                <Stack gap={4}>
                  <FilterBuilder idPrefix="bi-bar" catalog={catalog} text={text} value={bar} onChange={(next) => { setCrossNote(undefined); setBar({ ...next, having: [] }); }} refuse={compat} />
                  {(active.state.filters.length > 0 || active.state.anyOf.length > 0) && <Text as="p" role="caption" tone="secondary">{t('bi.flt.bar.note')}</Text>}
                  {crossNote !== undefined && <Notice tone="warning">{crossNote}</Notice>}
                  <FilterSetsMenu idPrefix="bi-bar" catalog={catalog} text={text} current={bar} allowHaving={false} onApply={(state) => { setCrossNote(undefined); setBar({ ...state, having: [] }); }} />
                  <ShareLinkButton state={urlState} text={text} />
                </Stack>
              </Panel>
            )}
            <UrlNotices sync={sync} text={text} />

            {noteSource !== undefined && <AnswerNotes view={noteSource} fallback={data.fallback} expectedMarkets={expected} text={text} />}

            <Grid min="sm" gap={4}>
              {panel.kpis.map((block, i) => {
                const tile = data.tiles[`kpi:${i}`];
                if (tile === undefined || tile.status === 'loading') return <KpiSkeletons key={block.id} metrics={block.metrics} text={text} />;
                if (tile.status === 'error') {
                  return (
                    <div key={block.id} data-span="all">
                      <BiProblem error={tile.error} onRetry={data.reload} text={text} />
                    </div>
                  );
                }
                return <KpiCards key={block.id} metrics={block.metrics} view={tile.value} text={text} />;
              })}
            </Grid>

            <Stack gap={4}>
              <Panel>
                <SectionTitle level={3} size="sub">{t(panel.trend.titleId)}</SectionTitle>
                {(() => {
                  const tile = data.tiles['trend'];
                  if (tile?.status === 'error') return <BiProblem error={tile.error} onRetry={data.reload} text={text} />;
                  return <TrendCharts view={tile?.status === 'ready' ? tile.value : undefined} grain={grain} metric={panel.trend.metric} title={t(panel.trend.titleId)} text={text} loading={tile === undefined || tile.status === 'loading'} />;
                })()}
              </Panel>
              <Grid min="lg" gap={4}>
                {panel.breakdowns.map((b) => {
                  const tile = data.tiles[`break:${b.id}`];
                  return (
                    <Panel key={b.id}>
                      <SectionTitle level={3} size="sub">{t(b.titleId)}</SectionTitle>
                      {tile?.status === 'error' ? (
                        <BiProblem error={tile.error} onRetry={data.reload} text={text} />
                      ) : tile?.status === 'ready' ? (
                        <BreakdownChart view={tile.value} metric={b.metric} dimension={b.dimension} kind={b.chart} title={t(b.titleId)} text={text} crossFilter={{ dimension: b.dimension, onSelect: onCrossFilter }} selected={selectedOf(b.dimension)} />
                      ) : (
                        <BarList items={[]} loading loadingLabel={t('ui.loading')} />
                      )}
                    </Panel>
                  );
                })}
              </Grid>
              {(panel.insights ?? []).length > 0 && (
                <Grid min="lg" gap={4} data-insights={panel.id}>
                  {(panel.insights ?? []).map((def) => (
                    <InsightCard
                      key={`${panel.id}:${def.id}`}
                      def={def}
                      tiles={data.tiles}
                      ctx={insightCtx}
                      onDrill={() => {
                        openInExplorer(insightDrillState(def, { period, compare, upToNowAgainst, scope: filters, bar: active.bar }, grain));
                        navigate('bi_explorer');
                      }}
                    />
                  ))}
                </Grid>
              )}
            </Stack>
          </Stack></TabPanel>
        </>
      )}
    </>
  );
}
